// /admin/waitlist: the operator approves people waiting. One row per person,
// checks on the left, and what to do with the selection on a bar floating at
// the bottom of the panel (on top of the table it would push the rows down
// each time). Approving mails each person and cannot be undone, so it asks
// once with the count. People are sent one by one; those refused stay
// selected.
import { useQuery } from "@tanstack/react-query";
import { m } from "@trameria/cloud-i18n";
import { m as common } from "@trameria/i18n";
import {
  Button,
  ErrorLine,
  iconOf,
  Skeleton,
  useApi,
  useFormat,
} from "@trameria/ui-core";
import { useRef, useState } from "react";
import type { AdminItem } from "../admin/admin";
import { useApprove, type WaitlistEntry, waitlistQuery } from "../queries";

const CHECK = "h-[14px] w-[14px] flex-none cursor-pointer accent-accent";
const CELL = "min-w-0 truncate text-[12.5px]";
// The date and the way in keep their width; the address takes the rest (and
// is cut first on a phone).
const COLUMNS = "20px minmax(0,1fr) 84px 56px";

/** The operator's waitlist, an item of /admin. */
export const waitlistAdminItem: AdminItem = {
  id: "waitlist",
  order: 10,
  label: () => m.web_admin_waitlist_title(),
  icon: iconOf("waiting"),
  description: () => m.web_admin_waitlist_description(),
  load: (ctx) =>
    ctx.queryClient
      .ensureQueryData(waitlistQuery(ctx.api))
      .catch(() => undefined),
  Component: AdminWaitlist,
};

function AdminWaitlist() {
  const api = useApi();
  const list = useQuery(waitlistQuery(api));
  if (list.isError)
    return (
      <div className="mt-6 flex items-center gap-3 text-[13px] text-muted">
        {m.web_load_failed()}
        <Button onClick={() => void list.refetch()}>{m.web_retry()}</Button>
      </div>
    );
  if (!list.data)
    return (
      <div className="mt-6">
        <Skeleton lines={[70, 55, 62]} />
      </div>
    );
  return <Table entries={list.data} />;
}

function Table({ entries }: { entries: WaitlistEntry[] }) {
  const format = useFormat();
  const approve = useApprove();
  const [picked, setPicked] = useState<ReadonlySet<string>>(new Set());
  const [confirming, setConfirming] = useState(false);
  /** How many the last approval went through for; shown until the next pick. */
  const [done, setDone] = useState<number | null>(null);
  const [failed, setFailed] = useState(false);
  /** Shift-click extends from here. */
  const anchor = useRef<number | null>(null);
  const shift = useRef(false);
  const listRef = useRef<HTMLDivElement>(null);
  const shown = entries.filter((e) => picked.has(e.userId));
  const allOn = entries.length > 0 && shown.length === entries.length;

  const reset = () => {
    setConfirming(false);
    setDone(null);
    setFailed(false);
  };

  function pick(index: number) {
    const withShift = shift.current;
    shift.current = false;
    reset();
    setPicked((prev) => {
      const next = new Set(prev);
      if (withShift && anchor.current !== null) {
        const [from, to] = [anchor.current, index].sort((a, b) => a - b);
        for (let i = from; i <= to; i++) next.add(entries[i].userId);
        return next;
      }
      const id = entries[index].userId;
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
    if (!withShift) anchor.current = index;
  }

  async function approveAll() {
    setConfirming(false);
    const refused: string[] = [];
    for (const e of shown) {
      try {
        await approve.mutateAsync(e.userId);
      } catch {
        refused.push(e.userId);
      }
    }
    // The bar loses the pressed button: the focus goes back to the table.
    listRef.current?.querySelector<HTMLInputElement>("input")?.focus();
    setPicked(new Set(refused));
    setFailed(refused.length > 0);
    setDone(shown.length - refused.length);
  }

  const markShift = (e: { shiftKey: boolean }) => {
    if (e.shiftKey) shift.current = true;
  };

  if (entries.length === 0 && done === null)
    return (
      <p className="mt-6 text-[13px] text-muted">
        {m.web_admin_waitlist_empty()}
      </p>
    );

  return (
    <>
      <div className="mt-5" ref={listRef}>
        <div
          className="grid items-center gap-x-[10px] px-[10px] pb-2 text-[11px] text-muted"
          style={{ gridTemplateColumns: COLUMNS }}
        >
          <input
            type="checkbox"
            aria-label={m.web_admin_waitlist_select_all()}
            className={CHECK}
            checked={allOn}
            onChange={() => {
              reset();
              anchor.current = null;
              setPicked(
                allOn ? new Set() : new Set(entries.map((e) => e.userId)),
              );
            }}
          />
          <span className={CELL}>{m.web_admin_waitlist_col_email()}</span>
          <span className={CELL}>{m.web_admin_waitlist_col_joined()}</span>
          <span className={CELL}>{m.web_admin_waitlist_col_via()}</span>
        </div>
        {entries.map((e, i) => (
          // biome-ignore lint/a11y/useKeyWithClickEvents: records Shift only; the checkbox inside does the choosing
          <label
            key={e.userId}
            className="grid cursor-pointer items-center gap-x-[10px] rounded-[7px] px-[10px] py-[7px] hover:bg-hover"
            style={{ gridTemplateColumns: COLUMNS }}
            onClick={markShift}
          >
            <input
              type="checkbox"
              aria-label={e.email}
              className={CHECK}
              checked={picked.has(e.userId)}
              onClick={markShift}
              onChange={() => pick(i)}
            />
            <span className={CELL}>{e.email}</span>
            <span className={`${CELL} tabular-nums`}>
              {format.day(e.createdAt)}
            </span>
            <span className={CELL}>
              {e.via === "google" ? "Google" : m.web_admin_waitlist_via_email()}
            </span>
          </label>
        ))}
      </div>
      {(shown.length > 0 || done !== null) && (
        <div className="absolute bottom-5 left-1/2 flex -translate-x-1/2 flex-col items-center gap-1">
          {failed && (
            <ErrorLine>{m.web_admin_waitlist_approve_failed()}</ErrorLine>
          )}
          <div
            role="toolbar"
            aria-label={m.web_admin_waitlist_toolbar()}
            className="flex items-center gap-[10px] whitespace-nowrap rounded-2xl border border-border bg-surface py-2 pr-2 pl-4 text-[12.5px] text-body shadow-[0_4px_16px_rgb(0_0_0/0.10)]"
          >
            {shown.length === 0 ? (
              <span className="pr-2 tabular-nums">
                {m.web_admin_waitlist_approved_done({ count: done ?? 0 })}
              </span>
            ) : confirming ? (
              <>
                <span className="tabular-nums">
                  {m.web_admin_waitlist_confirm_text({ count: shown.length })}
                </span>
                <Button
                  variant="primary"
                  disabled={approve.isPending}
                  onClick={() => void approveAll()}
                >
                  {m.web_admin_waitlist_confirm_approve()}
                </Button>
                <Button variant="ghost" onClick={() => setConfirming(false)}>
                  {common.common_cancel()}
                </Button>
              </>
            ) : (
              <>
                <span className="tabular-nums">
                  {m.web_admin_waitlist_selected({ count: shown.length })}
                </span>
                <Button
                  disabled={approve.isPending}
                  onClick={() => {
                    setFailed(false);
                    setConfirming(true);
                  }}
                >
                  {m.web_admin_waitlist_approve()}
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => {
                    listRef.current
                      ?.querySelector<HTMLInputElement>("input")
                      ?.focus();
                    anchor.current = null;
                    setPicked(new Set());
                  }}
                >
                  {m.web_admin_waitlist_clear()}
                </Button>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
